import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import { Alert, FormGroup, Label, Form, Button } from "reactstrap";
import { toast } from "react-hot-toast";
import PerfectScrollBar from "react-perfect-scrollbar";
import ReactSelect from "react-select";
import { setStylesValue } from "~/redux/actions";
import {
  PALLETE_STYLES,
  uppercaseFirstLetter,
  GEOJSON_STYLES,
  timer,
} from "~/utils";
import ColorsPicker from "~/components/ColorsPicker";
import request from "~/utils/request";

const EditStyle = ({ activeLayer, layers }) => {
  const { search } = useLocation();
  const { watch, handleSubmit, control, setValue } = useForm();

  const geoJSONLayers = layers?.leafletElement?.options?.data;

  const tipeQuerySearch = new URLSearchParams(search).get("tipe");
  const isHaveClasificationAttribute =
    activeLayer?.properties?.properties?.klasifikasi_atribut ?? false;

  const dispatch = useDispatch();

  const watchFillColor = watch("fillColor", null);
  const tematikLayer = activeLayer?.properties?.tipe?.tematik;

  const watchKlasifikasiName = activeLayer?.properties?.properties
    ?.klasifikasi_atribut_name
    ? watch(activeLayer?.properties?.properties?.klasifikasi_atribut_name, null)
    : null;

  const [loading, setLoading] = useState(false);

  const handleSaveStyle = async (values) => {
    const filterDesa = Object.fromEntries(
      Object.entries(activeLayer.properties).filter(
        ([key]) => !["desa", "tipe"].includes(key)
      )
    );

    let payloadWithClasification = {};

    if (isHaveClasificationAttribute) {
      payloadWithClasification = {
        properties: {
          ...Object.fromEntries(
            Object.entries(filterDesa.properties).filter(
              ([key]) =>
                !["ket_color", "warna"].concat(GEOJSON_STYLES).includes(key)
            )
          ),
          ...Object.fromEntries(
            Object.entries(
              values?.[
                activeLayer?.properties?.properties?.klasifikasi_atribut_name
              ]
            ).filter(([key]) => GEOJSON_STYLES.includes(key))
          ),
          [activeLayer?.properties?.properties?.klasifikasi_atribut_name]:
            values?.[
              activeLayer?.properties?.properties?.klasifikasi_atribut_name
            ]?.value,
        },
      };
    }

    if (tematikLayer) {
      const sigID = activeLayer?.properties?.id;

      setLoading(true);

      request
        .patch(`/v1/sig/${sigID}/`, payloadWithClasification)
        .then(() =>
          toast.success(
            `Style layer ${uppercaseFirstLetter(tipeQuerySearch)} ${
              activeLayer?.properties?.properties?.nama
            } berhasil diubah !`
          )
        )
        .finally(() => setLoading(false));
    } else {
      const featuresLength = geoJSONLayers?.features?.length;

      for (let [i, feature] of geoJSONLayers?.features?.entries()) {
        setLoading(true);

        const sigID = feature?.properties?.id;
        const payload = {
          properties: {
            ...Object.fromEntries(
              Object.entries(feature.properties.properties).filter(
                ([key]) => !["ket_color", "warna"].includes(key)
              )
            ),
            ...values,
          },
        };

        request.patch(`/v1/sig/${sigID}/`, payload).finally(() => {
          if (featuresLength === i + 1) {
            toast.success(
              `Style layer ${uppercaseFirstLetter(
                tipeQuerySearch
              )} berhasil diubah !`
            );
            setLoading(false);
          }
        });

        await timer(1500);
      }
    }
  };

  useEffect(() => {
    if (isHaveClasificationAttribute) {
      if (watchKlasifikasiName) {
        dispatch(
          setStylesValue({
            id: activeLayer?.properties?.id,
            [activeLayer?.properties?.properties?.klasifikasi_atribut_name]:
              watchKlasifikasiName?.value,
          })
        );
      }
    }
  }, [watchKlasifikasiName, isHaveClasificationAttribute]);

  useEffect(() => {
    if (isHaveClasificationAttribute) {
      setTimeout(() => {
        setValue(
          activeLayer?.properties?.properties?.klasifikasi_atribut_name,
          {
            label:
              activeLayer?.properties?.properties?.[
                activeLayer?.properties?.properties?.klasifikasi_atribut_name
              ],
            value:
              activeLayer?.properties?.properties?.[
                activeLayer?.properties?.properties?.klasifikasi_atribut_name
              ],
          }
        );
      }, 200);
    }
  }, [isHaveClasificationAttribute]);

  useEffect(() => {
    if (watchFillColor) {
      const payloadStyle = {
        fillColor: watchFillColor,
        id: activeLayer?.properties?.id,
      };

      dispatch(setStylesValue(payloadStyle));
    }
  }, [watchFillColor]);

  useEffect(() => {
    if (activeLayer) {
      const isProperties = activeLayer?.properties?.properties;

      Object.entries(isProperties)
        .filter(([key]) => GEOJSON_STYLES.includes(key))
        .forEach(([key, value]) => setValue(key, value));
    }
  }, [activeLayer]);

  if (!activeLayer) {
    return (
      <div style={{ height: "100vh" }}>
        <div className="p-2 d-flex align-items-center justify-content-center">
          <i className="mdi mdi-information-outline mr-1" />
          <p className="mb-0">silakan pilih layer untuk mengedit style.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ height: "100vh" }}>
      <Form onSubmit={handleSubmit(handleSaveStyle)}>
        <Alert className="text-center mb-0" color="primary">
          Edit Style Layer {uppercaseFirstLetter(tipeQuerySearch)}
          &nbsp;
          {tematikLayer &&
            uppercaseFirstLetter(activeLayer?.properties?.properties?.nama)}
        </Alert>
        <div className="d-flex px-3 justify-content-between align-items-center mt-3">
          <p className="mb-0">Tematik</p>
          <p className="mb-0">
            <i
              style={{ fontSize: "1.3rem" }}
              className={
                isHaveClasificationAttribute
                  ? "text-success mdi mdi-check"
                  : "text-danger mdi mdi-close"
              }
            />
          </p>
        </div>

        <div className="d-flex px-3 justify-content-between align-items-center mt-3">
          <p className="mb-0">Memiliki Klasifikasi Atribut</p>
          <p className="mb-0">
            <i
              style={{ fontSize: "1.3rem" }}
              className={
                tematikLayer
                  ? "text-success mdi mdi-check"
                  : "text-danger mdi mdi-close"
              }
            />
          </p>
        </div>

        <hr />
        <div style={{ height: "450px" }} className="mt-1 pb-4">
          <PerfectScrollBar>
            <div className="px-3 pt-1">
              {isHaveClasificationAttribute ? (
                <>
                  <FormGroup>
                    <Label>
                      {uppercaseFirstLetter(
                        activeLayer?.properties?.properties
                          ?.klasifikasi_atribut_name
                      )}
                    </Label>

                    <Controller
                      control={control}
                      name={
                        activeLayer?.properties?.properties
                          ?.klasifikasi_atribut_name
                      }
                      render={({ value, onChange }) => (
                        <ReactSelect
                          value={value}
                          onChange={(val) => onChange(val)}
                          options={activeLayer?.properties?.properties?.klasifikasi_atribut?.map(
                            (klasifikasi) => ({
                              ...klasifikasi,
                              label: klasifikasi.nama,
                              value: klasifikasi.nama,
                            })
                          )}
                        />
                      )}
                    />
                  </FormGroup>
                </>
              ) : (
                <>
                  <FormGroup>
                    <Label>Warna Peta</Label>
                    <Controller
                      name="fillColor"
                      control={control}
                      render={({ onChange, value }) => (
                        <ColorsPicker
                          colors={PALLETE_STYLES}
                          onChange={(value) => onChange(value)}
                        />
                      )}
                    />
                  </FormGroup>
                </>
              )}
            </div>
          </PerfectScrollBar>
        </div>

        <FormGroup
          className="mb-0"
          style={{ position: "absolute", bottom: 0, width: "100%" }}
        >
          <Button
            type="submit"
            size="lg"
            color="primary"
            block
            disabled={loading}
          >
            {loading ? "mengedit style..." : "Simpan"}
          </Button>
        </FormGroup>
      </Form>
    </div>
  );
};

export default EditStyle;
